import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { arrayMove } from "@dnd-kit/sortable";
import {
  validateDraftStep,
  validateMasterSuiteSteps,
} from "../../helper/masterSuiteValidation";
import {
  ancestorsOf,
  ensureExplicitParents,
  hasExplicitParents,
  healSelfParents,
  parentOptionsFor,
  relinkOnDelete,
  relinkOnRename,
  siblingsOf,
} from "../../helper/masterSuiteTree";
import {
  subCategories,
  processSubCategories,
  categories,
} from "../../constants/List Constants";
import {
  changeCategoryValue,
  changeSubCategoryValue,
} from "../../helper/string_helper";
import { getTaskByIdAndType } from "../../service/task.service";

// UI Automation subcategory options (only one).
const uiSubCategories = [{ label: "UI Test", value: "uiTest" }];

// All step categories including UI Automation.
const allCategories = [
  ...(categories || []),
  { label: "UI Automation", value: "ui" },
];

/**
 * Single source of truth for building master-suite steps. Owns the step rows and
 * every change handler, plus the shared Variables / Task / TaskView modal state.
 * Rendered surfaces (the editable table and the workflow canvas drawer) consume
 * this — so the table can later be removed without touching any logic.
 *
 * @param refs reference data fetched by the workspace:
 *   { taskList, testSuiteList, uiSuiteList, environments, folderPaths,
 *     defaultEnvironment, refreshTaskData, updateTaskData,
 *     newlyCreatedTask, resetNewlyCreatedTask }
 */
export function useMasterSuiteStepEditor(refs = {}) {
  const {
    taskList = [],
    testSuiteList = [],
    uiSuiteList = [],
    environments = [],
    folderPaths = [],
    defaultEnvironment = "",
    refreshTaskData = () => {},
    updateTaskData,
    newlyCreatedTask = null,
    resetNewlyCreatedTask = () => {},
  } = refs;

  const [dataSource, setDataSource] = useState([]);
  const [count, setCount] = useState(1);
  const [errorFields, setErrorFields] = useState({});

  // ── Draft / commit ────────────────────────────────────────────────────────
  // The drawer edits a DRAFT — a detached copy — and the graph only changes when the
  // draft is committed. Before this, every keystroke wrote straight into dataSource, so a
  // half-finished step WAS the graph: closing the drawer on an unnamed new step left a
  // committed orphan, and an invalid edit was already on the canvas by the time it was
  // rejected. Cancelling now costs nothing because nothing was ever written.
  //
  // { mode: "new" | "edit", intent, row }
  const [draft, setDraft] = useState(null);
  // The field handlers are shared with the Table view, which has no drawer and must keep
  // committing directly. A ref lets them decide where to write without being rewritten.
  const draftRef = useRef(null);
  draftRef.current = draft;

  /**
   * The single write seam for field handlers.
   *
   * `updater` is the usual rows-array function. When a draft is open AND this edit targets
   * it, the updater is applied to the draft alone; otherwise it goes to the committed
   * store exactly as before. That is what lets the drawer and the Table share one set of
   * handlers with opposite commit semantics.
   */
  /**
   * A drop-in replacement for setDataSource, bound to the row being edited.
   *
   * Field handlers call `rowWriter(record.key)(updater)` instead of `setDataSource(updater)`
   * — one token per handler — and automatically land on the draft when the drawer owns that
   * row, or on the committed store when the Table does.
   */
  const rowWriter = (targetKey) => (updater) => setRows(updater, targetKey);

  const setRows = (updater, targetKey) => {
    const d = draftRef.current;
    if (d && targetKey != null && d.row.key === targetKey) {
      setDraft((cur) => {
        const [next] = updater([cur.row]);
        return next ? { ...cur, row: next } : cur;
      });
      return;
    }
    setDataSource(updater);
  };

  // Shared modal / selection state.
  const [variablesVisible, setVariablesVisible] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [categoryValue, setCategoryValue] = useState("");
  const [subCategoryValue, setSubCategoryValue] = useState("");
  const [categoryKey, setCategoryKey] = useState("");
  const [subCategoryKey, setSubCategoryKey] = useState("");
  const [currentKey, setCurrentKey] = useState("");
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [isViewTaskOpen, setIsViewTaskOpen] = useState(false);
  const [viewTaskData, setViewTaskData] = useState({});

  // A task created from the Task Library page gets appended to the active row.
  useEffect(() => {
    if (!newlyCreatedTask) return;
    setDataSource((prev) =>
      prev.map((item) =>
        item.key === selectedRecord?.key
          ? {
              ...item,
              filteredTaskArray: [
                ...(item.filteredTaskArray || []),
                newlyCreatedTask,
              ],
              taskName: newlyCreatedTask.taskName,
            }
          : item,
      ),
    );
    resetNewlyCreatedTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newlyCreatedTask]);

  const getUniqueTaskNames = () => {
    const names = taskList.map((s) => s.taskName);
    return [...new Set(names)];
  };

  const reindex = (rows) => rows.map((item, i) => ({ ...item, sequence: i + 1 }));

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setDataSource((prev) => {
        const activeIndex = prev.findIndex((r) => r.key === active?.id);
        const overIndex = prev.findIndex((r) => r.key === over?.id);
        return reindex(arrayMove(prev, activeIndex, overIndex));
      });
    }
  };

  // Reorder by index — used by the drawer's Move up / Move down.
  const moveStep = (fromIndex, toIndex) => {
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= dataSource.length ||
      toIndex >= dataSource.length ||
      fromIndex === toIndex
    ) {
      return;
    }
    setDataSource((prev) => reindex(arrayMove(prev, fromIndex, toIndex)));
  };

  const updateDataSource = (key, value, record) => {
    rowWriter(record.key)((prev) =>
      prev.map((item) =>
        item.key === record.key ? { ...item, [key]: value } : item,
      ),
    );
  };

  // Blank step template — shared by append / insert / preset add.
  const makeBlank = (key, sequence) => ({
    key,
    sequence,
    type: null,
    category: null,
    subcategory: null,
    stepReference: null,
    taskName: null,
    variables: [],
    stopIfFailed: false,
    // Fan-out tree link — the parent step's stepReference, null for a root.
    // Left null here: while EVERY step is null the backend reads the suite as a chain by
    // sequence, so a linear suite saves and runs exactly as before. The drawer's
    // "Runs after" picker writes the chain down the moment a real branch is authored.
    parentId: null,
  });

  // ── Draft lifecycle ───────────────────────────────────────────────────────

  /** Open the drawer on an EXISTING step. Edits a detached copy; the graph is untouched. */
  const openDraftForEdit = (key) => {
    const row = dataSource.find((r) => r.key === key);
    if (!row) return null;
    const next = { mode: "edit", intent: { type: "edit" }, row: { ...row } };
    setDraft(next);
    // Set the ref NOW as well as on the next render: a caller may drive field handlers in
    // the same tick (the preset tiles do), and setDraft has not landed yet — those writes
    // would otherwise be aimed at the committed store and silently hit nothing.
    draftRef.current = next;
    setCurrentKey(key);
    setSelectedRecord(row);
    return key;
  };

  /**
   * Open the drawer on a NEW step. Nothing is added to the graph — `intent` records where
   * the step will go IF it is saved, and commitDraft applies it. Closing adds nothing,
   * which is the whole point: an abandoned add used to leave a committed nameless orphan.
   */
  const openDraftForNew = (intent = { type: "append" }) => {
    const newKey = count;
    setCount(newKey + 1);
    const row = makeBlank(newKey, dataSource.length + 1);
    const next = { mode: "new", intent, row };
    setDraft(next);
    draftRef.current = next; // see openDraftForEdit — same-tick handler calls need this
    setCurrentKey(newKey);
    setSelectedRecord(row);
    return newKey;
  };

  /** Throw the draft away. By construction there is nothing to undo. */
  const discardDraft = () => {
    setDraft(null);
    setSelectedRecord(null);
  };

  /** Field errors for the open draft, keyed by field — drives inline errors and Save. */
  const draftErrors = draft
    ? validateDraftStep(draft.row, dataSource, draft.mode)
    : {};
  const draftValid = draft ? Object.keys(draftErrors).length === 0 : false;

  /**
   * Where the draft lands. PURE — takes the rows, returns new rows, touches no state — so
   * the same placement can be previewed without committing. The parameters modal needs
   * that: a new step must be able to see which steps will run before it, and that is only
   * knowable by applying the intent.
   */
  const placeDraft = (prevRows, mode, intent, row) =>
    ((prev) => {
      if (mode === "edit") {
        const before = prev.find((r) => r.key === row.key);
        const replaced = prev.map((r) => (r.key === row.key ? row : r));
        // Children track the name only now that the rename is real.
        return healSelfParents(
          relinkOnRename(replaced, before?.stepReference, row.stepReference, row.key),
        );
      }
      if (intent.type === "child") {
        const rows = ensureExplicitParents(prev);
        const parent = rows.find((r) => r.key === intent.parentKey);
        return reindex([...rows, { ...row, parentId: parent?.stepReference || null }]);
      }
      if (intent.type === "insert") {
        const rows = [...prev];
        const at = Math.min(Math.max(intent.index + 1, 0), rows.length);
        const placed = { ...row };
        if (hasExplicitParents(prev) && intent.index >= 0 && intent.index < prev.length) {
          placed.parentId = prev[intent.index].stepReference || null;
        }
        rows.splice(at, 0, placed);
        // The downstream step now runs after the inserted one — the splice the old
        // pendingAdoptChildKey dance had to defer because the step had no name yet.
        const adopted =
          intent.childKey != null && hasExplicitParents(prev)
            ? rows.map((r) =>
                r.key === intent.childKey
                  ? { ...r, parentId: placed.stepReference }
                  : r,
              )
            : rows;
        return reindex(adopted);
      }
      // append
      const placed = { ...row };
      if (hasExplicitParents(prev) && prev.length) {
        placed.parentId = prev[prev.length - 1].stepReference || null;
      }
      return reindex([...prev, placed]);
    })(prevRows);

  /**
   * The suite as it WOULD be if the open draft were saved. Only used to answer questions
   * about a step that does not exist yet — never rendered on the canvas.
   */
  const projectedRows = draft
    ? placeDraft(dataSource, draft.mode, draft.intent, draft.row)
    : dataSource;

  /**
   * Write the draft into the graph. Returns false and changes NOTHING when invalid, so
   * every add/edit is atomic: validate → commit → recompute, or abort untouched.
   */
  const commitDraft = () => {
    if (!draft || !draftValid) return false;
    const { mode, intent, row } = draft;
    setDataSource((prev) => placeDraft(prev, mode, intent, row));
    setDraft(null);
    setSelectedRecord(null);
    setErrorFields({});
    return true;
  };

  // Append a new blank step. Returns the new row's key (or null if validation
  // blocks the add) so callers can open the editor on it.
  const handleAdd = () => {
    const validation = validateMasterSuiteSteps(dataSource);
    if (validation.hasErrors) {
      setErrorFields(validation.errorFields);
      return { error: validation.issues };
    }
    const newKey = count;
    setDataSource((prev) => {
      const blank = makeBlank(newKey, prev.length + 1);
      // Once the shape is authored, a null parent means "root" — it would start at the
      // same moment as step 1. Append onto the end of the list instead, which is what
      // "add step" has always meant; the picker can move it afterwards.
      if (hasExplicitParents(prev) && prev.length) {
        blank.parentId = prev[prev.length - 1].stepReference || null;
      }
      return [...prev, blank];
    });
    setCount(newKey + 1);
    return { key: newKey };
  };

  // Insert a blank step immediately AFTER index (the canvas "+" between steps).
  // Reindexes so `sequence` stays contiguous; returns the new row's key.
  // `childKey` is the step on the far side of the cable the "+" was clicked on, so the
  // new step can take its place in the chain. Omitted from the Table view, where the
  // list order is the chain and positional insertion already says it.
  const handleInsertAt = (index, childKey = null) => {
    const validation = validateMasterSuiteSteps(dataSource);
    if (validation.hasErrors) {
      setErrorFields(validation.errorFields);
      return { error: validation.issues };
    }
    const newKey = count;
    setDataSource((prev) => {
      const copy = [...prev];
      const at = Math.min(Math.max(index + 1, 0), copy.length);
      const blank = makeBlank(newKey, at + 1);
      if (hasExplicitParents(prev) && index >= 0 && index < prev.length) {
        blank.parentId = prev[index].stepReference || null;
        // It cannot adopt the downstream step yet — parentId is name-based and this step
        // has no name. handleStepReferenceChange completes the splice on first naming.
        if (childKey != null) blank.pendingAdoptChildKey = childKey;
      }
      copy.splice(at, 0, blank);
      return reindex(copy);
    });
    setCount(newKey + 1);
    return { key: newKey };
  };

  /**
   * Add a step that runs AFTER `parentKey` — a new branch off that step.
   *
   * Not handleInsertAt: that splices positionally, which on a linear suite means "insert
   * between" rather than "branch". This writes the parent link explicitly, so hovering a
   * card's "+" always produces a child of that card whatever the suite's shape.
   */
  const handleAddChild = (parentKey) => {
    const validation = validateMasterSuiteSteps(dataSource);
    if (validation.hasErrors) {
      setErrorFields(validation.errorFields);
      return { error: validation.issues };
    }
    const newKey = count;
    setDataSource((prev) => {
      // Without this every OTHER null-parent step would become a root the moment this one
      // gets a parent — see ensureExplicitParents.
      const rows = ensureExplicitParents(prev);
      const parent = rows.find((r) => r.key === parentKey);
      const blank = makeBlank(newKey, rows.length + 1);
      blank.parentId = parent?.stepReference || null;
      return reindex([...rows, blank]);
    });
    setCount(newKey + 1);
    return { key: newKey };
  };

  // Append a step pre-seeded from an empty-state tile, reusing the existing
  // change handlers (functional setState, so they apply in order after the
  // append) — no duplicated field logic.
  const handleAddPreset = (preset = {}) => {
    const result = handleAdd();
    if (result?.error) return result;
    const rec = { key: result.key };
    if (preset.type) handleTypeChange(preset.type, rec);
    if (preset.category && preset.type !== "Master Suite")
      handleCategoryChange(preset.category, rec);
    if (preset.subcategory && preset.category !== "ui")
      handleSubcategoryChange(preset.subcategory, rec);
    return result;
  };

  const handleDelete = (key) => {
    const keyStr = String(key);
    setDataSource((prev) => {
      const removed = prev.find((item) => String(item.key) === keyStr);
      const kept = prev.filter((item) => String(item.key) !== keyStr);
      return reindex(relinkOnDelete(kept, removed));
    });
  };

  const handleTypeChange = (value, record) => {
    rowWriter(record.key)((prev) =>
      prev.map((item) =>
        item.key === record.key
          ? {
              ...item,
              type: value,
              category: value === "Master Suite" ? "N/A" : null,
              subcategory: value === "Master Suite" ? "N/A" : null,
              variables: {},
              filteredTaskArray: [],
              taskName: null,
            }
          : item,
      ),
    );
  };

  const handleCategoryChange = (value, record) => {
    let subCategoryOptions = [];
    const categoryLabel = changeCategoryValue(value);

    switch (value) {
      case "compare":
        subCategoryOptions = subCategories;
        break;
      case "process":
        subCategoryOptions = processSubCategories;
        break;
      case "ui":
        subCategoryOptions = uiSubCategories;
        break;
      default:
        subCategoryOptions = [];
    }

    rowWriter(record.key)((prev) =>
      prev.map((item) =>
        item.key === record.key
          ? {
              ...item,
              category: value,
              subcategory: value === "ui" ? "uiTest" : null,
              // UI auto-selects its sub-category, so handleSubcategoryChange never
              // fires for it — set the label here or it saves blank.
              subCategoryValue:
                value === "ui" ? changeSubCategoryValue("uiTest") : null,
              subCategoryOptions,
              categoryValue: categoryLabel,
              filteredTaskArray:
                value === "ui"
                  ? (uiSuiteList || []).map((s) => ({
                      taskName: s.suiteName,
                      taskId: s.suiteId,
                      isUiSuite: true,
                    }))
                  : [],
              taskName: null,
            }
          : item,
      ),
    );
  };

  const handleSubcategoryChange = (value, record) => {
    setSelectedVariables([]);
    const subCatValue = changeSubCategoryValue(value);

    rowWriter(record.key)((prev) =>
      prev.map((item) => {
        if (item.key !== record.key) return item;

        let filteredTaskArray = [];
        if (value === "uiTest") {
          filteredTaskArray = (uiSuiteList || []).map((s) => ({
            taskName: s.suiteName,
            taskId: s.suiteId,
            isUiSuite: true,
          }));
        } else if (value === "file2spec") {
          filteredTaskArray = taskList.filter(
            (s) => s.taskSubCategory === "File to Specification",
          );
        } else if (value === "file2Db") {
          filteredTaskArray = taskList.filter(
            (s) => s.taskSubCategory === "File to Database",
          );
        } else if (value === "file2File") {
          filteredTaskArray = taskList.filter(
            (s) => s.taskSubCategory === "File to File",
          );
        } else if (value === "db2Db") {
          filteredTaskArray = taskList.filter(
            (s) => s.taskSubCategory === "Database to Database",
          );
        } else if (value === "eventMonitor") {
          filteredTaskArray = taskList.filter(
            (s) => s.taskSubCategory === "Event Monitor",
          );
        } else if (value === "move") {
          filteredTaskArray = taskList.filter(
            (s) => s.taskSubCategory === "File Move, Copy or Delete",
          );
        } else if (value === "extractCompressed") {
          filteredTaskArray = taskList.filter(
            (s) => s.taskSubCategory === "Extract Compressed File",
          );
        }

        return {
          ...item,
          subcategory: value,
          filteredTaskArray,
          subCategoryValue: subCatValue,
          taskName: null,
        };
      }),
    );
  };

  const handleStepReferenceChange = (value, record) => {
    // A DRAFT is not in the graph, so renaming it must not relink anyone. Just set the
    // name; commitDraft does the relinking once the step actually exists. This is also
    // what makes a half-typed name harmless — nothing downstream can see it.
    const d = draftRef.current;
    if (d && d.row.key === record.key) {
      setDraft((cur) => ({ ...cur, row: { ...cur.row, stepReference: value } }));
      return;
    }
    const isDuplicate = dataSource.some(
      (item) => item.stepReference === value && item.key !== record.key,
    );
    setErrorFields((prev) => ({
      ...prev,
      [record.key]: {
        stepReference: isDuplicate ? "Step name already exists" : undefined,
      },
    }));

    let updated = dataSource
      .map((item) => {
        if (item.key === record.key) {
          const updatedItem = { ...item, stepReference: value };
          if (item.variables && typeof item.variables === "object") {
            updatedItem.variables = {
              ...item.variables,
              stepReference: value,
              inputFromOne:
                updatedItem.variables.inputFromOne === item.stepReference
                  ? value
                  : updatedItem.variables.inputFromOne,
              inputFromTwo:
                updatedItem.variables.inputFromTwo === item.stepReference
                  ? value
                  : updatedItem.variables.inputFromTwo,
            };
          }
          return updatedItem;
        }
        return item;
      })
      .map((item) => {
        if (item.variables && typeof item.variables === "object") {
          item.variables = {
            ...item.variables,
            inputFromOne:
              item.variables.inputFromOne === record.stepReference
                ? value
                : item.variables.inputFromOne,
            inputFromTwo:
              item.variables.inputFromTwo === record.stepReference
                ? value
                : item.variables.inputFromTwo,
          };
        }
        return item;
      });
    // Children follow the rename — see relinkOnRename for why this is not optional. The
    // key is what stops the renamed step re-pointing its OWN parent at itself when its
    // name momentarily matches its parent's ("test" on the way to "test 2").
    updated = relinkOnRename(updated, record.stepReference, value, record.key);

    // A step inserted between two others could not adopt its downstream step at insert
    // time — it had no name yet, and parentId is name-based. Now that it has one, hand
    // the recorded child over, so "insert between" really does end up in the middle.
    const inserted = updated.find((item) => item.key === record.key);
    const adoptKey = inserted?.pendingAdoptChildKey;
    const final =
      adoptKey != null && value
        ? updated.map((item) => {
            if (item.key === adoptKey) return { ...item, parentId: value };
            if (item.key === record.key) {
              const { pendingAdoptChildKey, ...rest } = item;
              return rest;
            }
            return item;
          })
        : updated;

    // Deliberately NOT healed here. Mid-word a step's name can equal its parent's ("test"
    // on the way to "test 2"), and healing that instant would null a perfectly good parent
    // link that never comes back once the names diverge again. The selfKey guard above is
    // what prevents corruption while editing; healing is for data loaded from the server.
    setDataSource(final);
  };

  /**
   * Set which step this one waits for. `value` is a stepReference, or null to make it
   * start a branch of its own.
   *
   * ensureExplicitParents runs first and is the important part: while every parentId is
   * null the backend treats the suite as a chain by sequence, but the instant ONE step
   * has a parent that stops applying and every other null-parent step becomes a root
   * that starts immediately. Writing the implied chain down first means setting one
   * link changes exactly one thing.
   */
  /**
   * Canvas drag-and-drop: drop step `childKey` onto step `parentKey` to make it run
   * after that step.
   *
   * The legality rule lives HERE rather than in the canvas so there is one gate. The
   * canvas greys out illegal targets, but a drop must never be able to create a cycle
   * just because a hover state raced — a cycle would not error at run time, it would
   * simply mean neither step is ever scheduled.
   *
   * Returns false when nothing changed, so the caller can skip a re-render.
   */
  const handleReparent = (childKey, parentKey) => {
    const child = dataSource.find((r) => r.key === childKey);
    const parent = dataSource.find((r) => r.key === parentKey);
    if (!child || !parent?.stepReference) return false;
    if (child.parentId === parent.stepReference) return false; // already there
    const allowed = parentOptionsFor(dataSource, child).some(
      (r) => r.key === parent.key,
    );
    if (!allowed) return false;
    handleParentChange(parent.stepReference, child);
    return true;
  };

  const handleParentChange = (value, record) => {
    // Same reasoning as the rename: ensureExplicitParents rewrites the WHOLE suite, which
    // a draft has no business doing. Record the choice; commitDraft applies it.
    const d = draftRef.current;
    if (d && d.row.key === record.key) {
      setDraft((cur) => ({ ...cur, row: { ...cur.row, parentId: value || null } }));
      return;
    }
    setDataSource((prev) =>
      ensureExplicitParents(prev).map((item) =>
        item.key === record.key ? { ...item, parentId: value || null } : item,
      ),
    );
  };

  const handleTaskNameChange = (value, record) => {
    updateDataSource("taskName", value, record);
    setSelectedRecord(record);
  };

  const handleVariablesClick = (record) => {
    if (record.category === "ui") {
      message.info(
        "UI Automation steps do not require variable configuration.",
      );
      return;
    }
    if (!defaultEnvironment?.length) {
      message.error("Please select default environment to proceed.");
      return;
    }
    if (record.type === "Master Suite") {
      message.error("Master Suite cannot have variables. Please select task.");
      return;
    }
    if (!record.taskName) {
      message.error(
        "Please select a task to proceed with configuring variables.",
      );
      return;
    }
    const subCatValue = record.subcategory || "";
    const subCatLabel = changeSubCategoryValue(subCatValue);
    setCurrentKey(record.key);
    setSubCategoryValue(subCatLabel);
    setSubCategoryKey(subCatValue);
    setSelectedRecord(record);
    setSelectedVariables(record.variables || []);
    setVariablesVisible(true);
  };

  const handleVariablesOnClose = (visible) => setVariablesVisible(visible);

  const handleSaveVariables = (setVariables, record) => {
    const variables = {
      ...setVariables,
      categoryValue: record.categoryValue,
      subCategoryValue: record.subCategoryValue,
      stepReference: record.stepReference,
      defaultEnvironmentOne:
        setVariables.environmentOne === defaultEnvironment,
      defaultEnvironmentTwo:
        setVariables.environmentTwo === defaultEnvironment,
    };
    rowWriter(record.key)((prev) =>
      prev.map((item) =>
        item.key === record.key ? { ...item, variables } : item,
      ),
    );
    setVariablesVisible(false);
  };

  const handleAddTaskItem = (record) => {
    if (record.category === "ui") {
      message.info(
        "Create new UI automation suites from the UI Automation page.",
      );
      return;
    }
    const categoryLabel = changeCategoryValue(record.category);
    const subCatLabel = changeSubCategoryValue(record.subcategory || "");
    setCategoryValue(record.categoryValue || "");
    setSubCategoryValue(subCatLabel);
    setCategoryKey(categoryLabel);
    setSubCategoryKey(record.subcategory || "");
    setSelectedRecord(record);
    setTaskModalVisible((prev) => !prev);
  };

  const handleSwitchChange = (checked, record) => {
    rowWriter(record.key)((prev) =>
      prev.map((item) =>
        item.key === record.key ? { ...item, stopIfFailed: checked } : item,
      ),
    );
  };

  const handleTaskCreated = (newTask) => {
    setDataSource((prev) =>
      prev.map((item) =>
        item.key === selectedRecord?.key
          ? {
              ...item,
              filteredTaskArray: [...(item.filteredTaskArray || []), newTask],
              taskName: newTask.taskName,
            }
          : item,
      ),
    );
    refreshTaskData(newTask);
    setTaskModalVisible(false);
  };

  const onClickViewTask = (record) => {
    if (record.category === "ui") {
      message.info("View UI automation suites from the UI Automation page.");
      return;
    }
    if (!record.taskName) {
      message.info("Select a task on this step first.");
      return;
    }
    // Resolve the id from the live taskList first. The row's filteredTaskArray is
    // only a snapshot taken when that row's sub-category was last changed, so it can
    // be empty or stale — which silently resolved to the wrong id (or null) and made
    // the viewer show another step's task.
    const byName = (list) =>
      (list || []).find((t) => t.taskName === record.taskName)?.taskId;
    const taskId = byName(taskList) ?? byName(record.filteredTaskArray) ?? null;
    const category = record.categoryValue || changeCategoryValue(record.category);

    if (!taskId || !category) {
      message.error(
        `Couldn't find details for "${record.taskName}". Re-select the task on this step.`,
      );
      return;
    }

    getTaskByIdAndType(taskId, category)
      .then((response) => {
        setViewTaskData(response.data);
        setIsViewTaskOpen(true);
      })
      .catch(() =>
        message.error("Something went wrong. Please try again later!"),
      );
  };

  return {
    // state
    dataSource,
    setDataSource,
    errorFields,
    allCategories,
    // ── draft / commit (the drawer) ──
    // The drawer renders `draft.row`; the canvas renders `dataSource`. They only meet at
    // commitDraft, which is what makes cancelling free and every add atomic.
    draft,
    draftErrors,
    draftValid,
    openDraftForEdit,
    openDraftForNew,
    commitDraft,
    discardDraft,
    // handlers
    handleAdd,
    handleAddChild,
    handleInsertAt,
    handleAddPreset,
    handleDelete,
    onDragEnd,
    moveStep,
    handleTypeChange,
    handleCategoryChange,
    handleSubcategoryChange,
    handleStepReferenceChange,
    handleParentChange,
    handleReparent,
    // Tree queries the drawer needs. Bound to the live dataSource here so no caller has
    // to remember to pass it — and so the "can't pick your own descendant" rule (the
    // thing that makes cycles unreachable) can't be forgotten at a call site.
    parentOptionsFor: (record) => parentOptionsFor(dataSource, record),
    siblingsOf: (record) => siblingsOf(dataSource, record),
    handleTaskNameChange,
    handleSwitchChange,
    handleVariablesClick,
    handleAddTaskItem,
    onClickViewTask,
    getUniqueTaskNames,
    // shared modal prop bundles (rendered once by the workspace)
    variablesModalProps: {
      open: variablesVisible,
      onClose: handleVariablesOnClose,
      environments,
      subCategoryValues: subCategoryValue,
      // Only this step's ANCESTORS can be read from. A sibling branch runs at the same
      // time, so its output may or may not exist yet — offering it would let someone
      // build a suite that resolves differently from one run to the next.
      // This is narrower than before for linear suites too: the old list also offered
      // LATER steps, whose output cannot exist when this step runs.
      // projectedRows, not dataSource: a NEW step is not in the graph yet, so asking
      // dataSource which steps run before it would answer "none" and the parameters modal
      // would offer nothing until the step had been saved and reopened.
      stepNames: ancestorsOf(projectedRows, { key: currentKey })
        .filter((item) => item.category !== "compare")
        .map((item) => item.stepReference),
      defaultEnvironment,
      onSave: (variables) => handleSaveVariables(variables, selectedRecord),
      variables: selectedVariables,
      folderPaths,
      eventType:
        selectedRecord?.variables?.eventType ||
        selectedRecord?.eventType ||
        "",
    },
    taskModalProps: {
      isOpen: taskModalVisible,
      // TaskModal calls props.openModal(false) to close, so this MUST be the boolean
      // setter — not handleAddTaskItem (a toggle that ignored the arg and re-opened the
      // modal right after a successful create). Opening still goes through handleAddTaskItem.
      openModal: setTaskModalVisible,
      suiteData: testSuiteList,
      taskNames: getUniqueTaskNames(),
      refreshTaskData,
      disabledCategory: true,
      disabledSubCategory: true,
      categoryValue,
      categoryKey,
      subCategoryValue,
      subCategoryKey,
      updateTaskData,
      onTaskCreated: handleTaskCreated,
    },
    taskViewModalProps: {
      isOpen: isViewTaskOpen,
      handleModal: () => setIsViewTaskOpen((prev) => !prev),
      data: viewTaskData,
    },
  };
}

export default useMasterSuiteStepEditor;
